import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { createBrowserHistory } from "history";

// Create browser history for Application Insights
const browserHistory = createBrowserHistory();

// Create React plugin for Application Insights
const reactPlugin = new ReactPlugin();

// Application Insights configuration
const appInsights = new ApplicationInsights({
  config: {
    connectionString: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || "",

    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: {
        history: browserHistory,
      },
    },

    // Enable automatic route change tracking
    enableAutoRouteTracking: true,

    // Enable automatic unhandled promise rejection tracking
    enableUnhandledPromiseRejectionTracking: true,

    // Enable correlation headers for distributed tracing
    enableCorsCorrelation: true,

    // Enable AJAX performance tracking (HTTP requests)
    enableAjaxPerfTracking: true,

    // Sample rate for telemetry (1.0 = 100%, 0.1 = 10%)
    samplingPercentage: process.env.NODE_ENV === "production" ? 10 : 100,

    // Disable telemetry in demo
    disableTelemetry: process.env.NODE_ENV === "demo",
  },
});

// Helper functions for data sanitization and user context
const sanitizeUrl = (url: string): string =>
  // Remove potential PHI from URLs (MRN, patient IDs, etc.)
  url
    .replace(/\/encounter\/[^\/]+/g, "/encounter/[ID]")
    .replace(/\/patient-encounters\/[^\/]+/g, "/patient-encounters/[ID]")
    .replace(/\/create\/[^\/]+\/[^\/]+/g, "/create/[MRN]/[FACILITY]")
    .replace(/mrn=[^&]+/g, "mrn=[REDACTED]")
    .replace(/patientId=[^&]+/g, "patientId=[REDACTED]")
    .replace(/MedicalRecordNumber=[^&]+/g, "MedicalRecordNumber=[REDACTED]");
const sanitizePageName = (pageName: string): string =>
  // Remove potential PHI from page names
  pageName
    .replace(/Encounter \d+/g, "Encounter [ID]")
    .replace(/Patient \d+/g, "Patient [ID]")
    .replace(/MRN: \w+/g, "MRN: [REDACTED]");
const getUserId = (): string => {
  // Get non-PHI user identifier (email or user ID)
  try {
    // Try to get user email from MSAL or other auth provider
    if (window.useAzureADAuth && (window as any).msal) {
      const account = (window as any).msal.getActiveAccount();
      return account?.username || "anonymous";
    }
    return "anonymous";
  } catch {
    return "anonymous";
  }
};

const getSessionId = (): string => {
  // Generate or retrieve session ID
  let sessionId = sessionStorage.getItem("ai-session-id");
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem("ai-session-id", sessionId);
  }
  return sessionId;
};

// Initialize Application Insights only if connection string is provided
if (process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING) {
  appInsights.loadAppInsights();

  // Add telemetry initializer for PHI compliance and user context
  appInsights.addTelemetryInitializer((envelope: any) => {
    // Should we do sanitize this data?
    // Sanitize URLs that might contain PHI
    if (envelope.baseData?.url) {
      envelope.baseData.url = sanitizeUrl(envelope.baseData.url);
    }

    // Should we do sanitize this data?
    // Sanitize page names that might contain PHI
    if (envelope.baseData?.name) {
      envelope.baseData.name = sanitizePageName(envelope.baseData.name);
    }

    // Add cloud role context and user context (non-PHI)
    if (envelope.tags) {
      envelope.tags["ai.cloud.role"] = "TEC-Frontend";
      envelope.tags["ai.cloud.roleInstance"] = window.location.hostname;
      envelope.tags["ai.user.id"] = getUserId();
      envelope.tags["ai.session.id"] = getSessionId();
    }

    return true;
  });

  console.log("Application Insights initialized for TEC Frontend");
} else {
  console.warn(
    "Application Insights not initialized - missing connection string or instrumentation key",
  );
}

// Export the Application Insights instance and React plugin
export { appInsights, browserHistory, reactPlugin };

// Export tracking functions for custom telemetry
export const trackEvent = (
  name: string,
  properties?: Record<string, any>,
  measurements?: Record<string, number>,
) => {
  appInsights.trackEvent({ name, properties, measurements });
};

export const trackException = (exception: Error, properties?: Record<string, any>) => {
  appInsights.trackException({ exception, properties });
};

export const trackTrace = (
  message: string,
  severityLevel?: number,
  properties?: Record<string, any>,
) => {
  appInsights.trackTrace({ message, severityLevel, properties });
};

export const trackPageView = (name?: string, uri?: string, properties?: Record<string, any>) => {
  appInsights.trackPageView({ name, uri, properties });
};

export const trackDependency = (
  dependencyTypeName: string,
  name: string,
  data: string,
  duration: number,
  success: boolean,
  resultCode?: number,
) => {
  appInsights.trackDependencyData({
    id: `${dependencyTypeName}_${name}_${Date.now()}`,
    target: name,
    name: dependencyTypeName,
    data,
    duration,
    success,
    responseCode: resultCode || 0,
  });
};

// Custom tracking for TEC-specific events
export const trackUserAction = (action: string, page: string, properties?: Record<string, any>) => {
  trackEvent("UserAction", {
    action,
    page,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

export const trackEncounterAction = (
  action: string,
  encounterType?: string,
  properties?: Record<string, any>,
) => {
  trackEvent("EncounterAction", {
    action,
    encounterType,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

export const trackSearchAction = (
  searchType: string,
  resultsCount?: number,
  properties?: Record<string, any>,
) => {
  trackEvent(
    "SearchAction",
    {
      searchType,
      timestamp: new Date().toISOString(),
      ...properties,
    },
    {
      resultsCount: resultsCount || 0,
    },
  );
};

export const trackPerformance = (
  name: string,
  duration: number,
  properties?: Record<string, any>,
) => {
  trackEvent(
    "Performance",
    {
      name,
      timestamp: new Date().toISOString(),
      ...properties,
    },
    {
      duration,
    },
  );
};
